import { Copy, RefreshCw, X } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { cn } from "../../lib/utils";

export type OverlayKind = "translation" | "summary" | "explain";

export interface Overlay {
  kind: OverlayKind;
  title: string;
  lang?: string;
  streaming?: boolean;
  cached?: boolean;
  content: string;
}

const KIND_STYLES: Record<OverlayKind, string> = {
  translation: "border-(--info)",
  summary: "border-(--accent-500)",
  explain: "border-(--warning)",
};

interface BlockProps {
  overlay: Overlay;
  onClose: () => void;
  onCopy?: () => void;
  onRegen?: () => void;
}

export function InlineOverlayBlock({ overlay, onClose, onCopy, onRegen }: BlockProps) {
  return (
    <div
      className={cn(
        "my-3 rounded-lg border-l-4 bg-(--bg-elevated) px-4 py-3 text-sm shadow-(--elev-1)",
        KIND_STYLES[overlay.kind],
      )}
    >
      <header className="mb-1 flex items-center gap-2 text-xs">
        <strong className="text-(--text-primary)">{overlay.title}</strong>
        {overlay.lang && (
          <Badge variant="muted" className="text-[10px]">
            {overlay.lang}
          </Badge>
        )}
        {overlay.cached && (
          <Badge variant="outline" className="text-[10px]">
            cached
          </Badge>
        )}
        {overlay.streaming && (
          <span className="text-(--text-tertiary) animate-pulse">streaming…</span>
        )}
        <span className="ml-auto flex items-center gap-1">
          {onCopy && (
            <button
              type="button"
              onClick={onCopy}
              className="rounded p-1 text-(--text-tertiary) hover:bg-(--bg-hover)"
              aria-label="Copiar"
            >
              <Copy size={12} />
            </button>
          )}
          {onRegen && (
            <button
              type="button"
              onClick={onRegen}
              className="rounded p-1 text-(--text-tertiary) hover:bg-(--bg-hover)"
              aria-label="Regenerar"
            >
              <RefreshCw size={12} />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-(--text-tertiary) hover:bg-(--bg-hover)"
            aria-label="Cerrar"
          >
            <X size={12} />
          </button>
        </span>
      </header>
      <div className="whitespace-pre-wrap text-(--text-primary)">{overlay.content}</div>
    </div>
  );
}

/** Empty container — overlays render where ProseBody decides. */
export function InlineOverlay() {
  return null;
}
